import React, { Component } from 'react';
import { Shield, Download, Settings, Users, Database, FileText, Clock, CheckCircle, AlertCircle, Activity, ArrowRight, Plus, Trash2, RefreshCw, Mail, UserCheck, History } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppButton } from '../components/base/AppButton';
// --- Mock Data & Types ---
type RequestStatus = 'Pending' | 'In Progress' | 'Completed';
type RequestType = 'Access' | 'Deletion' | 'Portability' | 'Rectification';
interface DataRequest {
  id: string;
  type: RequestType;
  user: string;
  email: string;
  status: RequestStatus;
  timestamp: string;
  description: string;
}
interface ConsentCategory {
  name: string;
  consentRate: number;
  totalUsers: number;
  trend: number;
}
interface RetentionPolicy {
  dataType: string;
  retentionDays: number;
  nextCleanup: string;
  itemsCount: number;
  progress: number;
}
interface ActivityItem {
  id: string;
  type: 'consent' | 'request' | 'policy' | 'system';
  description: string;
  user: string;
  timestamp: string;
}
const MOCK_REQUESTS: DataRequest[] = [{
  id: 'REQ-082',
  type: 'Deletion',
  user: 'Marco Rossi',
  email: 'm.rossi@example.com',
  status: 'Pending',
  timestamp: '2023-10-24T10:30:00Z',
  description: 'Richiesta di cancellazione totale dei dati personali e storico chat.'
}, {
  id: 'REQ-081',
  type: 'Access',
  user: 'Laura Bianchi',
  email: 'l.bianchi@example.com',
  status: 'In Progress',
  timestamp: '2023-10-23T15:45:00Z',
  description: 'Richiesta copia dei dati di profilazione e log di sistema.'
}, {
  id: 'REQ-080',
  type: 'Portability',
  user: 'Giuseppe Verdi',
  email: 'g.verdi@example.com',
  status: 'Completed',
  timestamp: '2023-10-21T09:15:00Z',
  description: 'Esportazione dati in formato JSON per trasferimento.'
}, {
  id: 'REQ-079',
  type: 'Rectification',
  user: 'Anna Neri',
  email: 'a.neri@example.com',
  status: 'Completed',
  timestamp: '2023-10-20T14:20:00Z',
  description: 'Aggiornamento preferenze di contatto e dati anagrafici.'
}];
const MOCK_CONSENTS: ConsentCategory[] = [{
  name: 'Marketing & Newsletter',
  consentRate: 68,
  totalUsers: 12450,
  trend: 2.4
}, {
  name: 'Analytics & Performance',
  consentRate: 85,
  totalUsers: 15500,
  trend: 0.5
}, {
  name: 'Third-party Sharing',
  consentRate: 42,
  totalUsers: 7680,
  trend: -1.2
}, {
  name: 'AI Profiling & Training',
  consentRate: 74,
  totalUsers: 13540,
  trend: 5.8
}];
const MOCK_RETENTION: RetentionPolicy[] = [{
  dataType: 'Chat History',
  retentionDays: 90,
  nextCleanup: 'Oggi',
  itemsCount: 45200,
  progress: 98
}, {
  dataType: 'System Logs',
  retentionDays: 30,
  nextCleanup: 'Domani',
  itemsCount: 128000,
  progress: 85
}, {
  dataType: 'User Analytics',
  retentionDays: 365,
  nextCleanup: 'Tra 5 giorni',
  itemsCount: 8500,
  progress: 45
}, {
  dataType: 'Inactive Accounts',
  retentionDays: 730,
  nextCleanup: 'Tra 12 giorni',
  itemsCount: 340,
  progress: 20
}];
const MOCK_ACTIVITIES: ActivityItem[] = [{
  id: 'act-1',
  type: 'request',
  description: 'Nuova richiesta di cancellazione dati',
  user: 'Marco Rossi',
  timestamp: '10 min fa'
}, {
  id: 'act-2',
  type: 'system',
  description: 'Esecuzione automatica data cleanup (Logs)',
  user: 'System',
  timestamp: '2 ore fa'
}, {
  id: 'act-3',
  type: 'consent',
  description: 'Aggiornamento massivo consensi AI Training',
  user: 'Admin',
  timestamp: 'Ieri, 15:30'
}, {
  id: 'act-4',
  type: 'policy',
  description: 'Modifica retention policy per Chat History',
  user: 'Admin',
  timestamp: 'Ieri, 11:15'
}, {
  id: 'act-5',
  type: 'request',
  description: 'Completata esportazione dati (Portability)',
  user: 'Giuseppe Verdi',
  timestamp: '21 Ott, 09:15'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: RequestStatus;
}) => {
  switch (status) {
    case 'Completed':
      return <AppBadge variant="green" data-id="element-2403">
          <CheckCircle className="w-3 h-3 mr-1" data-id="element-2404" /> Completata
        </AppBadge>;
    case 'In Progress':
      return <AppBadge variant="blue" data-id="element-2405">
          <RefreshCw className="w-3 h-3 mr-1" data-id="element-2406" /> In Lavorazione
        </AppBadge>;
    case 'Pending':
      return <AppBadge variant="amber" data-id="element-2407">
          <Clock className="w-3 h-3 mr-1" data-id="element-2408" /> In Attesa
        </AppBadge>;
    default:
      return <AppBadge variant="default" data-id="element-2409">{status}</AppBadge>;
  }
};
const TypeIcon = ({
  type
}: {
  type: RequestType;
}) => {
  switch (type) {
    case 'Deletion':
      return <Trash2 className="w-4 h-4 text-red-400" data-id="element-2410" />;
    case 'Access':
      return <FileText className="w-4 h-4 text-blue-400" data-id="element-2411" />;
    case 'Portability':
      return <Download className="w-4 h-4 text-purple-400" data-id="element-2412" />;
    case 'Rectification':
      return <UserCheck className="w-4 h-4 text-amber-400" data-id="element-2413" />;
  }
};
const ActivityIcon = ({
  type
}: {
  type: ActivityItem['type'];
}) => {
  switch (type) {
    case 'request':
      return <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center" data-id="element-2414">
          <Mail className="w-3 h-3 text-blue-400" data-id="element-2415" />
        </div>;
    case 'system':
      return <div className="w-6 h-6 rounded-full bg-gray-500/10 flex items-center justify-center" data-id="element-2416">
          <Settings className="w-3 h-3 text-gray-400" data-id="element-2417" />
        </div>;
    case 'consent':
      return <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center" data-id="element-2418">
          <Shield className="w-3 h-3 text-green-400" data-id="element-2419" />
        </div>;
    case 'policy':
      return <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center" data-id="element-2420">
          <FileText className="w-3 h-3 text-amber-400" data-id="element-2421" />
        </div>;
  }
};
// --- Main Page Component ---
export function GDPRPage() {
  return <div className="min-h-screen bg-[#121212] pb-12" data-id="element-2422">
      {/* Header Section */}
      <div className="pt-6 px-4 md:px-8 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2423">
        <div data-id="element-2424">
          <h1 className="text-[20px] font-semibold text-white tracking-tight" data-id="element-2425">
            GDPR Compliance
          </h1>
        </div>

        <div className="flex items-center gap-2" data-id="element-2426">
          <AppButton variant="secondary" size="sm" icon={<Download className="w-4 h-4" data-id="element-2428" />} title="Export Report" data-id="element-2427">
            Export
          </AppButton>
          <AppButton variant="secondary" size="sm" icon={<Settings className="w-4 h-4" data-id="element-2430" />} title="Impostazioni GDPR" data-id="element-2429">
            Impostazioni
          </AppButton>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div className="px-4 md:px-8 py-4" data-id="element-2431">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2432">
          {/* Richieste Totali */}
          <AppCard padding="compact" data-id="element-2433">
            <div className="flex items-center gap-2 mb-2" data-id="element-2434">
              <Users className="w-4 h-4 text-white/40" data-id="element-2435" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2436">
                Richieste Totali
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2437">
              <p className="font-bold text-white text-[20px]" data-id="element-2438">1,248</p>
            </div>
          </AppCard>

          {/* In Attesa */}
          <AppCard padding="compact" data-id="element-2439">
            <div className="flex items-center gap-2 mb-2" data-id="element-2440">
              <AlertCircle className="w-4 h-4 text-white/40" data-id="element-2441" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2442">
                In Attesa
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2443">
              <p className="font-bold text-white text-[20px]" data-id="element-2444">12</p>
            </div>
          </AppCard>

          {/* Consent Rate */}
          <AppCard padding="compact" data-id="element-2445">
            <div className="flex items-center gap-2 mb-2" data-id="element-2446">
              <CheckCircle className="w-4 h-4 text-white/40" data-id="element-2447" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2448">
                Consent Rate
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2449">
              <p className="font-bold text-white text-[20px]" data-id="element-2450">84%</p>
            </div>
          </AppCard>

          {/* Retention Compliance */}
          <AppCard padding="compact" data-id="element-2451">
            <div className="flex items-center gap-2 mb-2" data-id="element-2452">
              <Database className="w-4 h-4 text-white/40" data-id="element-2453" />
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-2454">
                Retention Compliance
              </span>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-2455">
              <p className="font-bold text-white text-[20px]" data-id="element-2456">99.9%</p>
            </div>
          </AppCard>
        </div>
      </div>

      <div className="px-4 md:px-8 space-y-6" data-id="element-2457">
        {/* Quick Actions Section */}
        <div data-id="element-2458">
          <h2 className="text-[14px] font-semibold text-white mb-4" data-id="element-2459">
            Azioni Rapide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2460">
            <AppCard padding="compact" hover className="group" onClick={() => {}} data-id="element-2461">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2462">
                <Plus className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2463" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2464">
                Nuova Richiesta Dati
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2465">
                Registra manualmente una richiesta di accesso, cancellazione o
                portabilità.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2466">
                Registra richiesta{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2467" />
              </div>
            </AppCard>

            <AppCard padding="compact" hover className="group" onClick={() => {}} data-id="element-2468">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2469">
                <Users className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2470" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2471">
                Gestisci Consensi
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2472">
                Visualizza e modifica le categorie di consenso e le preferenze
                degli utenti.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2473">
                Gestisci categorie{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2474" />
              </div>
            </AppCard>

            <AppCard padding="compact" hover className="group" onClick={() => {}} data-id="element-2475">
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] group-hover:bg-[#8eff01]/10 flex items-center justify-center mb-4 transition-colors" data-id="element-2476">
                <Database className="w-5 h-5 text-white/70 group-hover:text-[#8eff01] transition-colors" data-id="element-2477" />
              </div>
              <h3 className="text-[14px] font-medium text-white mb-1" data-id="element-2478">
                Policy Retention
              </h3>
              <p className="text-[12px] text-white/40 mb-4" data-id="element-2479">
                Configura le regole di conservazione e cancellazione automatica
                dei dati.
              </p>
              <div className="flex items-center text-[12px] font-medium text-[#8eff01]" data-id="element-2480">
                Configura policy{' '}
                <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2481" />
              </div>
            </AppCard>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2482">
          {/* Left Column (Span 2) */}
          <div className="lg:col-span-2 space-y-6" data-id="element-2483">
            {/* Data Subject Requests Dashboard */}
            <AppCard padding="none" className="overflow-hidden flex flex-col" data-id="element-2484">
              <div className="p-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-2485">
                <div data-id="element-2486">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2487">
                    Richieste Interessati (DSR)
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2488">
                    Ultime richieste di esercizio dei diritti privacy
                  </p>
                </div>
                <AppButton variant="secondary" size="sm" data-id="element-2489">
                  Vedi tutte
                </AppButton>
              </div>

              <div className="p-5 bg-white/[0.01]" data-id="element-2490">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2491">
                  {MOCK_REQUESTS.map(req => <AppCard key={req.id} padding="compact" hover className="flex flex-col h-full border-white/[0.08] bg-[#1a1a1a]" data-id="element-2492">
                      <div className="flex items-start justify-between mb-3" data-id="element-2493">
                        <div className="flex items-center gap-2" data-id="element-2494">
                          <div className="p-1.5 bg-white/[0.04] rounded-md" data-id="element-2495">
                            <TypeIcon type={req.type} data-id="element-2496" />
                          </div>
                          <div data-id="element-2497">
                            <span className="text-[13px] font-medium text-white block" data-id="element-2498">
                              {req.type}
                            </span>
                            <span className="text-[10px] text-white/40" data-id="element-2499">
                              {req.id}
                            </span>
                          </div>
                        </div>
                        <StatusBadge status={req.status} data-id="element-2500" />
                      </div>

                      <div className="mb-4 flex-1" data-id="element-2501">
                        <p className="text-[13px] text-white/80 font-medium" data-id="element-2502">
                          {req.user}
                        </p>
                        <p className="text-[11px] text-white/40 mb-2" data-id="element-2503">
                          {req.email}
                        </p>
                        <p className="text-[12px] text-white/60 line-clamp-2 leading-relaxed" data-id="element-2504">
                          {req.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-white/[0.06] mt-auto" data-id="element-2505">
                        <span className="text-[11px] text-white/30 flex items-center gap-1" data-id="element-2506">
                          <Clock className="w-3 h-3" data-id="element-2507" />
                          {new Date(req.timestamp).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                        </span>
                        <button className="text-[12px] font-medium text-white/70 hover:text-white transition-colors" data-id="element-2508">
                          Gestisci
                        </button>
                      </div>
                    </AppCard>)}
                </div>
              </div>
            </AppCard>

            {/* Consent Management Overview */}
            <AppCard data-id="element-2509">
              <div className="flex items-center justify-between mb-6" data-id="element-2510">
                <div data-id="element-2511">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2512">
                    Panoramica Consensi
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2513">
                    Tassi di accettazione per categoria
                  </p>
                </div>
              </div>

              <div className="space-y-5" data-id="element-2514">
                {MOCK_CONSENTS.map((consent, idx) => <div key={idx} data-id="element-2515">
                    <div className="flex items-center justify-between mb-2" data-id="element-2516">
                      <div className="flex items-center gap-2" data-id="element-2517">
                        <span className="text-[13px] font-medium text-white/80" data-id="element-2518">
                          {consent.name}
                        </span>
                        <span className="text-[10px] text-white/40 px-1.5 py-0.5 bg-white/[0.04] rounded" data-id="element-2519">
                          {consent.totalUsers.toLocaleString()} utenti
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-2520">
                        <span className={`text-[11px] ${consent.trend > 0 ? 'text-[#8eff01]' : 'text-red-400'}`} data-id="element-2521">
                          {consent.trend > 0 ? '+' : ''}
                          {consent.trend}%
                        </span>
                        <span className="text-[13px] font-bold text-white" data-id="element-2522">
                          {consent.consentRate}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 w-full bg-white/[0.04] rounded-full overflow-hidden" data-id="element-2523">
                      <div className="h-full bg-[#8eff01] rounded-full transition-all duration-500" style={{
                    width: `${consent.consentRate}%`
                  }} data-id="element-2524" />
                    </div>
                  </div>)}
              </div>
            </AppCard>
          </div>

          {/* Right Column (Span 1) */}
          <div className="space-y-6" data-id="element-2525">
            {/* Data Retention Status */}
            <AppCard data-id="element-2526">
              <div className="flex items-center justify-between mb-6" data-id="element-2527">
                <div data-id="element-2528">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2529">
                    Data Retention
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2530">
                    Stato policy di conservazione
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-2531">
                {MOCK_RETENTION.map((policy, idx) => <div key={idx} className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg" data-id="element-2532">
                    <div className="flex items-center justify-between mb-2" data-id="element-2533">
                      <span className="text-[13px] font-medium text-white" data-id="element-2534">
                        {policy.dataType}
                      </span>
                      <span className="text-[11px] text-white/40" data-id="element-2535">
                        {policy.retentionDays} gg
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-3" data-id="element-2536">
                      <span className="text-[11px] text-white/60" data-id="element-2537">
                        {policy.itemsCount.toLocaleString()} record
                      </span>
                      <span className={`text-[11px] font-medium ${policy.nextCleanup === 'Oggi' ? 'text-amber-400' : 'text-[#8eff01]'}`} data-id="element-2538">
                        Cleanup: {policy.nextCleanup}
                      </span>
                    </div>

                    <div className="h-1.5 w-full bg-white/[0.04] rounded-full overflow-hidden" data-id="element-2539">
                      <div className={`h-full rounded-full transition-all duration-500 ${policy.progress > 90 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                    width: `${policy.progress}%`
                  }} data-id="element-2540" />
                    </div>
                  </div>)}
              </div>
            </AppCard>

            {/* Recent Activity Feed */}
            <AppCard data-id="element-2541">
              <div className="flex items-center justify-between mb-6" data-id="element-2542">
                <div data-id="element-2543">
                  <h2 className="text-[15px] font-semibold text-white" data-id="element-2544">
                    Attività Recenti
                  </h2>
                  <p className="text-[12px] text-white/40 mt-0.5" data-id="element-2545">
                    Log eventi privacy e GDPR
                  </p>
                </div>
                <History className="w-4 h-4 text-white/30" data-id="element-2546" />
              </div>

              <div className="relative pl-3 border-l border-white/[0.08] space-y-6 ml-2" data-id="element-2547">
                {MOCK_ACTIVITIES.map(activity => <div key={activity.id} className="relative" data-id="element-2548">
                    <div className="absolute -left-[25px] top-0 bg-[#1a1a1a] py-1" data-id="element-2549">
                      <ActivityIcon type={activity.type} data-id="element-2550" />
                    </div>
                    <div className="pl-2" data-id="element-2551">
                      <p className="text-[13px] text-white/80 leading-snug mb-1" data-id="element-2552">
                        {activity.description}
                      </p>
                      <div className="flex items-center gap-2 text-[11px]" data-id="element-2553">
                        <span className="text-white/60 font-medium" data-id="element-2554">
                          {activity.user}
                        </span>
                        <span className="text-white/20" data-id="element-2555">•</span>
                        <span className="text-white/40" data-id="element-2556">
                          {activity.timestamp}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>

              <button className="w-full mt-6 py-2 text-[12px] font-medium text-white/40 hover:text-white transition-colors border-t border-white/[0.06]" data-id="element-2557">
                Vedi tutto lo storico
              </button>
            </AppCard>
          </div>
        </div>
      </div>
    </div>;
}